import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatPlaceholderValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((entry) => formatPlaceholderValue(entry)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const EmailSendDetailDialog = ({
  open,
  detail,
  loading,
  onClose,
  onDelete,
  onRetry,
  onRefresh,
  retrying = false,
  refreshing = false,
  showDelete = false,
  showRetry = false,
  showRefresh = false,
  eventOperation = null,
  onEventOperation,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email Send Details</DialogTitle>
      <DialogContent dividers>
        {loading && <Alert severity="info">Loading details…</Alert>}
        {!loading && detail && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Sent</Typography>
                <Typography variant="body2">
                  {formatDateTime(detail.createdAt ?? detail.created_at)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Status</Typography>
                <Typography variant="body2">{detail.status || '—'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Trigger</Typography>
                <Typography variant="body2">{detail.triggerName || '—'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Template</Typography>
                <Typography variant="body2">{detail.templateName || '—'}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="subtitle2">Subject</Typography>
              <Typography variant="body1">{detail.subjectRendered || '—'}</Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">To</Typography>
                <Typography variant="body2">{(detail.toRecipients || []).join(', ') || '—'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">CC</Typography>
                <Typography variant="body2">{(detail.ccRecipients || []).join(', ') || '—'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">BCC</Typography>
                <Typography variant="body2">{(detail.bccRecipients || []).join(', ') || '—'}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="subtitle2">Preview</Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                {detail.textRendered || '—'}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2">Resolved Placeholders</Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 320,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: 12,
                }}
              >
                {detail.resolvedPlaceholders
                  ? Object.entries(detail.resolvedPlaceholders)
                    .sort(([left], [right]) => left.localeCompare(right))
                    .map(([key, value]) => `${key}: ${formatPlaceholderValue(value)}`)
                    .join('\n')
                  : '—'}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {showRefresh && (
          <Button
            color="primary"
            startIcon={<AutorenewIcon />}
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh template'}
          </Button>
        )}
        {showRetry && (
          <Button
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? 'Retrying…' : 'Retry Send'}
          </Button>
        )}
        {eventOperation && (
          <Button
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => onEventOperation?.(detail, eventOperation)}
          >
            {eventOperation === 'resend'
              ? 'Resend event email'
              : 'Retry event email'}
          </Button>
        )}
        {showDelete && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
          >
            Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailSendDetailDialog;
